import { forwardRef, type MouseEventHandler, type ReactNode } from "react"
import { ArrowRight } from "@phosphor-icons/react"
import { cn } from "~/utils/helpers"

type QuantityDiffCellProps = {
  /** Current value, pre-formatted by the caller (e.g. formatNumber, "-"). */
  value: ReactNode
  /** Previous/suggested value shown struck-through with an arrow before `value`.
   *  Omit (or pass null) to show `value` alone. */
  previousValue?: ReactNode | null
  /** Drives the border + transparent background when true. */
  canEdit: boolean
  /** Highlights the box with the warning background (e.g. MOQ violation). */
  hasViolation?: boolean
  /** Outlines the box for a minor violation (colli size). Ignored while `hasViolation` is set.
   *  Placeholder treatment until SUP-415 settles the tokens. */
  hasMinorViolation?: boolean
  /** PO always renders the border regardless of `canEdit`; Fashion/NOOS only when editable. */
  alwaysShowBorder?: boolean
  /** Box width. Defaults to 90% of the cell (adaptive to column width); PO overrides to a fixed narrower box. */
  widthClassName?: string
  /** Escape hatch for caller-specific modifiers (e.g. PO's `shrink-0 overflow-hidden focus:bg-base`). */
  className?: string
  /** Click-to-edit callers (Budget) open their own editor on click; AG Grid callers don't need this. */
  onClick?: MouseEventHandler<HTMLDivElement>
}

/** Display-only quantity cell: current value, optionally with a struck-through previous
 * value + arrow, optionally highlighted for a validation violation. Shared box for
 * AG Grid cellRenderers backed by a native cellEditor (Fashion/NOOS/PO) and for click-to-edit
 * cells (Budget); each caller keeps its own Tooltip/Badge/icon wrapping and edit UI around
 * this. Forwards its ref to the root box so callers can measure it (e.g. for a
 * truncation-tooltip). */
export const QuantityDiffCell = forwardRef<HTMLDivElement, QuantityDiffCellProps>(
  function QuantityDiffCell(
    {
      value,
      previousValue,
      canEdit,
      hasViolation = false,
      hasMinorViolation = false,
      alwaysShowBorder = false,
      widthClassName = "w-[90%]",
      className,
      onClick,
    },
    ref,
  ) {
    const showBorder = canEdit || alwaysShowBorder
    return (
      <div
        ref={ref}
        onClick={onClick}
        className={cn(
          "flex items-center rounded-lg px-2 py-1 mt-0.75 h-8 min-w-0 overflow-hidden",
          widthClassName,
          showBorder && "border border-solid border-base focus:outline-none",
          hasViolation ? "bg-(--color-warning)" : showBorder ? "bg-transparent" : undefined,
          hasMinorViolation && !hasViolation && "border border-dashed border-(--color-warning)",
          className,
        )}
      >
        {previousValue != null ? (
          <div className="flex items-center gap-0.5">
            <span className="line-through text-fg-disabled">{previousValue}</span>
            <ArrowRight size={12} className="text-fg-disabled shrink-0" />
            <span>{value}</span>
          </div>
        ) : (
          value
        )}
      </div>
    )
  },
)
